import React, { useState, useEffect, useMemo } from 'react';
import { 
  Package, 
  CheckCircle2, 
  AlertTriangle, 
  Clock, 
  ChevronDown, 
  ChevronUp,
  Plus,
  X,
  CalendarClock,
  CheckCheck,
  HelpCircle,
  Info,
  AlertCircle,
  Loader2,
  RefreshCw,
  Sparkles,
  Calendar,
  Gauge,
  History
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * ServiceRecommendationPanel - Phase 2 UI for the Service Interval Recommendation Engine
 * 
 * Displays a structured 4-section recommendation:
 * 1. Primary Service Package (oil + filter - locked)
 * 2. Additional Due Now (auto-added items)
 * 3. Important Separate Items (major items requiring decision)
 * 4. Future / Plan Next (planning items)
 * 
 * Design principle: "The service recommendation has already been built for me"
 */

const API = process.env.REACT_APP_BACKEND_URL;

// Quality indicator badge styling
const QUALITY_STYLES = {
  well_supported: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-700',
    icon: CheckCircle2,
    label: 'Well Supported'
  },
  partial_history: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    icon: Info,
    label: 'Partial History'
  },
  history_limited: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-600',
    icon: HelpCircle,
    label: 'Limited History'
  }
};

// Confidence level styling
const CONFIDENCE_STYLES = {
  confirmed_due: { bg: 'bg-green-100', text: 'text-green-700', label: 'Confirmed' },
  likely_due: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Likely' },
  unverified: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Unverified' }
};

// Section styling and icons - NEW STRUCTURE (March 2026)
const SECTION_CONFIG = {
  service_items_due: {
    title: 'Service Items Due',
    subtitle: 'Confirmed overdue based on records',
    icon: AlertTriangle,
    bgClass: 'bg-red-50',
    borderClass: 'border-red-200',
    headerClass: 'bg-red-100',
    iconColor: 'text-red-600'
  },
  service_items_history_unknown: {
    title: 'Service Items - History Unknown',
    subtitle: 'Recommended as we have no service history on record',
    icon: HelpCircle,
    bgClass: 'bg-amber-50',
    borderClass: 'border-amber-200',
    headerClass: 'bg-amber-100',
    iconColor: 'text-amber-600',
    hasDisclaimer: true
  },
  important_to_consider: {
    title: 'Important to Consider',
    subtitle: 'Items worth discussing with customer',
    icon: AlertCircle,
    bgClass: 'bg-orange-50',
    borderClass: 'border-orange-200',
    headerClass: 'bg-orange-100',
    iconColor: 'text-orange-600'
  },
  future_plan_next: {
    title: 'Future / Plan Next',
    subtitle: 'Items approaching due date',
    icon: CalendarClock,
    bgClass: 'bg-slate-50',
    borderClass: 'border-slate-200',
    headerClass: 'bg-slate-100',
    iconColor: 'text-slate-600'
  }
};

// FOH Action buttons
const ACTION_CONFIG = {
  include_now: { label: 'Include', icon: Plus, color: 'bg-green-600 hover:bg-green-700 text-white' },
  defer: { label: 'Not Now', icon: CalendarClock, color: 'bg-amber-100 hover:bg-amber-200 text-amber-700' },
  already_done_elsewhere: { label: 'Done Elsewhere', icon: CheckCheck, color: 'bg-slate-100 hover:bg-slate-200 text-slate-700' },
  not_applicable: { label: 'N/A', icon: X, color: 'bg-slate-100 hover:bg-slate-200 text-slate-500' },
  requires_discussion: { label: 'Discuss', icon: HelpCircle, color: 'bg-blue-100 hover:bg-blue-200 text-blue-700' }
};

// Individual recommendation item component
const RecommendationItem = ({ item, section, onAction, isLoading, isInPricing, showIntervalInfo = false }) => {
  const [expanded, setExpanded] = useState(false);
  const confidenceStyle = CONFIDENCE_STYLES[item.confidence_level] || CONFIDENCE_STYLES.unverified;
  
  // Build explanation text - special handling for previously declined
  const getExplanationText = () => {
    if (item.previously_declined) {
      if (item.confidence_level === 'confirmed_due') {
        return `Previously not actioned; confirmed due based on records`;
      } else if (item.confidence_level === 'likely_due') {
        return `Previously recommended; likely still due based on available history`;
      } else {
        return `Previously not actioned; still due based on current records`;
      }
    }
    return item.explanation_text;
  };
  
  const handleAction = (action) => {
    onAction(item.item_id, action, section, item);
  };
  
  // Get available actions - modify if already in pricing
  const getAvailableActions = () => {
    if (isInPricing) {
      // Item already added - only show informational state
      return [];
    }
    return item.available_actions || [];
  };

  // Format interval info for display
  const getIntervalDisplay = () => {
    if (!item.interval_months && !item.interval_miles) return null;
    const parts = [];
    if (item.interval_months) parts.push(`${item.interval_months} months`);
    if (item.interval_miles) parts.push(`${item.interval_miles.toLocaleString()} miles`);
    return parts.join(' / ');
  };

  // Calculate how close to due
  const getDueProgress = () => {
    if (!item.interval_months || !item.months_since) return null;
    const progress = Math.min((item.months_since / item.interval_months) * 100, 100);
    return Math.round(progress);
  };
  
  return (
    <div className={`border rounded-lg p-3 mb-2 transition-all ${
      isInPricing 
        ? 'bg-green-50 border-green-300' 
        : item.previously_declined 
          ? 'bg-orange-50 border-orange-200'
          : item.is_auto_included 
            ? 'bg-white' 
            : 'bg-slate-50'
    }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-slate-800">{item.item_label}</span>
            
            {/* Already in pricing indicator */}
            {isInPricing && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-600 text-white font-medium">
                In Pricing
              </span>
            )}
            
            {/* Previously declined - DISTINCT PRESENTATION */}
            {item.previously_declined && !isInPricing && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500 text-white font-medium">
                Previously Recommended
              </span>
            )}
            
            {/* Confidence badge - only show if not in pricing */}
            {!isInPricing && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${confidenceStyle.bg} ${confidenceStyle.text}`}>
                {confidenceStyle.label}
              </span>
            )}
            
            {/* Auto-included indicator */}
            {item.is_auto_included && !isInPricing && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                Auto-included
              </span>
            )}
            
            {/* Major item flag */}
            {item.is_major_item && !isInPricing && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                Major Item
              </span>
            )}
          </div>
          
          {/* Explanation text - special styling for previously declined */}
          <p className={`text-xs mt-1 ${
            item.previously_declined && !isInPricing
              ? 'text-orange-600 font-medium'
              : isInPricing 
                ? 'text-green-600' 
                : 'text-slate-500'
          }`}>
            {isInPricing ? 'Already added to this estimate' : getExplanationText()}
          </p>
          
          {/* INLINE INTERVAL INFO for Future/Plan Next section */}
          {showIntervalInfo && !isInPricing && (item.interval_months || item.interval_miles) && (
            <div className="mt-2 p-2 bg-slate-100 rounded-md border border-slate-200">
              <div className="flex items-center gap-4 text-xs">
                {/* Service Interval */}
                <div className="flex items-center gap-1.5">
                  <Calendar size={12} className="text-slate-500" />
                  <span className="text-slate-600">
                    <strong>Interval:</strong> {getIntervalDisplay()}
                  </span>
                </div>
                
                {/* Current Status */}
                {(item.months_since || item.miles_since) && (
                  <div className="flex items-center gap-1.5">
                    <Gauge size={12} className="text-slate-500" />
                    <span className="text-slate-600">
                      <strong>Current:</strong>{' '}
                      {item.months_since ? `${Math.round(item.months_since)} months` : ''} 
                      {item.months_since && item.miles_since ? ' / ' : ''}
                      {item.miles_since ? `${item.miles_since.toLocaleString()} miles` : ''}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Progress bar */}
              {getDueProgress() !== null && (
                <div className="mt-2">
                  <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                    <span>Progress to next service</span>
                    <span>{getDueProgress()}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        getDueProgress() >= 90 ? 'bg-red-500' : 
                        getDueProgress() >= 75 ? 'bg-amber-500' : 'bg-blue-500'
                      }`}
                      style={{ width: `${getDueProgress()}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Expandable details */}
          {expanded && !isInPricing && (
            <div className="mt-2 pt-2 border-t border-slate-200 text-xs text-slate-600 space-y-1">
              {item.history_note && <p><strong>History:</strong> {item.history_note}</p>}
              {item.interval_months && <p><strong>Interval:</strong> {item.interval_months} months / {item.interval_miles?.toLocaleString()} miles</p>}
              {item.months_since && <p><strong>Since last:</strong> {item.months_since.toFixed(0)} months / {item.miles_since?.toLocaleString() || 'N/A'} miles</p>}
              {item.due_reasons?.length > 0 && <p><strong>Due reasons:</strong> {item.due_reasons.join('; ')}</p>}
              {item.previously_declined && (
                <p className="text-orange-600">
                  <strong>Previously recommended:</strong> {item.previously_declined_date?.slice(0, 10) || 'Date unknown'}
                  {item.previously_declined_reason && ` - ${item.previously_declined_reason}`}
                </p>
              )}
            </div>
          )}
        </div>
        
        {/* Expand toggle - hide if in pricing */}
        {!isInPricing && (
          <button 
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-slate-200 rounded shrink-0"
          >
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        )}
      </div>
      
      {/* Action buttons - hide if already in pricing */}
      {!isInPricing && getAvailableActions().length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-200">
          {getAvailableActions().map(action => {
            const config = ACTION_CONFIG[action];
            if (!config) return null;
            
            const isDefault = action === item.default_action;
            const Icon = config.icon;
            
            return (
              <button
                key={action}
                onClick={() => handleAction(action)}
                disabled={isLoading}
                className={`
                  flex items-center gap-1 px-2 py-1 rounded text-xs font-medium
                  ${config.color}
                  ${isDefault ? 'ring-2 ring-offset-1 ring-blue-400' : ''}
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all
                `}
              >
                {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
                {config.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Section component
const RecommendationSection = ({ sectionKey, items, onAction, isLoading, defaultExpanded = true, isItemInPricing }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const config = SECTION_CONFIG[sectionKey];
  const Icon = config.icon;
  
  if (!items || items.length === 0) return null;
  
  // Count items already in pricing
  const inPricingCount = items.filter(item => isItemInPricing(item.item_id)).length;
  
  // Show interval info for Future/Plan Next section
  const showIntervalInfo = sectionKey === 'future_plan_next';
  
  return (
    <div className={`rounded-lg border ${config.borderClass} overflow-hidden mb-4`}>
      {/* Section header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full ${config.headerClass} px-4 py-3 flex items-center justify-between`}
      >
        <div className="flex items-center gap-2">
          <Icon size={18} className={config.iconColor} />
          <div className="text-left">
            <h3 className="font-semibold text-sm text-slate-800">{config.title}</h3>
            <p className="text-xs text-slate-500">{config.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {inPricingCount > 0 && (
            <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded-full">
              {inPricingCount} added
            </span>
          )}
          <span className="text-xs font-medium text-slate-600 bg-white px-2 py-0.5 rounded-full">
            {items.length} item{items.length !== 1 ? 's' : ''}
          </span>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>
      
      {/* Section content */}
      {expanded && (
        <div className={`${config.bgClass} p-3`}>
          {/* Add All to Pricing button - only show when items not all in pricing */}
          {items.length > 0 && inPricingCount < items.length && (
            <div className="mb-3 flex items-center justify-between bg-white border border-green-200 rounded-lg px-3 py-2">
              <span className="text-xs text-slate-600">
                {items.length - inPricingCount} item{items.length - inPricingCount !== 1 ? 's' : ''} not yet in pricing
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  items.forEach(item => {
                    if (!isItemInPricing(item.item_id)) {
                      onAction(item.item_id, 'include_now', sectionKey, item);
                    }
                  });
                }}
                className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
                data-testid={`add-all-to-pricing-${sectionKey}`}
              >
                <Plus size={14} />
                Add All to Pricing
              </button>
            </div>
          )}
          {items.map((item, idx) => (
            <RecommendationItem
              key={item.item_id || idx}
              item={item}
              section={sectionKey}
              onAction={onAction}
              isLoading={isLoading}
              isInPricing={isItemInPricing(item.item_id)}
              showIntervalInfo={showIntervalInfo}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Quality indicator component
const QualityIndicator = ({ quality, explanation }) => {
  const style = QUALITY_STYLES[quality] || QUALITY_STYLES.history_limited;
  const Icon = style.icon;
  
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${style.bg} ${style.border}`}>
      <Icon size={16} className={style.text} />
      <div>
        <span className={`text-sm font-medium ${style.text}`}>{style.label}</span>
        <p className="text-xs text-slate-500">{explanation}</p>
      </div>
    </div>
  );
};

// Alert component - now expandable with item details
const RecommendationAlert = ({ alert, items = [], defaultExpanded = false, onAction, isItemInPricing }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  const severityStyles = {
    high: 'bg-red-50 border-red-200 text-red-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    info: 'bg-blue-50 border-blue-200 text-blue-700'
  };
  
  const style = severityStyles[alert.severity] || severityStyles.info;
  const hasItems = items && items.length > 0;
  
  const handleInclude = (item) => {
    if (onAction) {
      onAction(item.item_id, 'include_now', 'alert', item);
    }
  };
  
  // Format interval info for display
  const getIntervalDisplay = (item) => {
    const parts = [];
    if (item.interval_months) parts.push(`${item.interval_months} months`);
    if (item.interval_miles) parts.push(`${item.interval_miles?.toLocaleString()} miles`);
    return parts.join(' / ') || 'Not specified';
  };
  
  // Format current status (how overdue)
  const getCurrentStatus = (item) => {
    const parts = [];
    if (item.months_since) parts.push(`${Math.round(item.months_since)} months`);
    if (item.miles_since) parts.push(`${item.miles_since?.toLocaleString()} miles`);
    return parts.join(' / ') || 'Unknown';
  };
  
  // Calculate how overdue
  const getOverdueAmount = (item) => {
    if (item.interval_months && item.months_since) {
      const overdueMonths = Math.round(item.months_since - item.interval_months);
      if (overdueMonths > 0) return `${overdueMonths} months overdue`;
    }
    if (item.interval_miles && item.miles_since) {
      const overdueMiles = item.miles_since - item.interval_miles;
      if (overdueMiles > 0) return `${overdueMiles.toLocaleString()} miles overdue`;
    }
    return null;
  };
  
  return (
    <div className={`rounded border ${style} overflow-hidden`}>
      <button
        onClick={() => hasItems && setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-3 py-2 text-sm ${hasItems ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
      >
        <div className="flex items-center gap-2">
          <AlertCircle size={14} />
          <span>{alert.message}</span>
        </div>
        {hasItems && (
          <div className="flex items-center gap-1">
            <span className="text-xs opacity-70">({items.length})</span>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </div>
        )}
      </button>
      
      {/* Expanded item list with action buttons and interval info */}
      {hasItems && expanded && (
        <div className={`px-3 pb-3 pt-1 border-t ${
          alert.severity === 'high' ? 'border-red-200 bg-red-50/50' :
          alert.severity === 'warning' ? 'border-amber-200 bg-amber-50/50' :
          'border-blue-200 bg-blue-50/50'
        }`}>
          {/* Add All to Pricing button */}
          {(() => {
            const notInPricingCount = items.filter(i => !(isItemInPricing ? isItemInPricing(i.item_id) : false)).length;
            return notInPricingCount > 0 && onAction ? (
              <div className="mb-2 flex items-center justify-between bg-white border border-green-200 rounded-lg px-3 py-2">
                <span className="text-xs text-slate-600">
                  {notInPricingCount} item{notInPricingCount !== 1 ? 's' : ''} not yet in pricing
                </span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    items.forEach(item => {
                      if (!(isItemInPricing ? isItemInPricing(item.item_id) : false)) {
                        handleInclude(item);
                      }
                    });
                  }}
                  className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
                  data-testid={`add-all-to-pricing-alert`}
                >
                  <Plus size={14} />
                  Add All to Pricing
                </button>
              </div>
            ) : null;
          })()}
          <div className="space-y-2">
            {items.map((item, idx) => {
              const alreadyInPricing = isItemInPricing ? isItemInPricing(item.item_id) : false;
              const overdueAmount = getOverdueAmount(item);
              
              return (
                <div key={idx} className={`bg-white rounded-lg px-3 py-2.5 border ${
                  alreadyInPricing ? 'border-green-300 bg-green-50' : 'border-slate-200'
                }`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-slate-800">{item.item_label || item.item_id}</span>
                        {alreadyInPricing && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-600 text-white font-medium">
                            In Pricing
                          </span>
                        )}
                        {overdueAmount && !alreadyInPricing && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-600 text-white font-medium">
                            {overdueAmount}
                          </span>
                        )}
                      </div>
                      
                      {/* Interval Info - WHEN, HOW, WHY */}
                      {(item.interval_months || item.interval_miles) && (
                        <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-100 text-xs">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                            {/* WHEN - Recommended Interval */}
                            <div className="flex items-center gap-1.5">
                              <Calendar size={11} className="text-slate-400" />
                              <span className="text-slate-500">Interval:</span>
                              <span className="font-medium text-slate-700">{getIntervalDisplay(item)}</span>
                            </div>
                            
                            {/* HOW - Current Status */}
                            <div className="flex items-center gap-1.5">
                              <Gauge size={11} className="text-slate-400" />
                              <span className="text-slate-500">Since last:</span>
                              <span className="font-medium text-slate-700">{getCurrentStatus(item)}</span>
                            </div>
                            
                            {/* Last done date if known */}
                            {item.last_done_date && (
                              <div className="flex items-center gap-1.5 col-span-2">
                                <History size={11} className="text-slate-400" />
                                <span className="text-slate-500">Last done:</span>
                                <span className="font-medium text-slate-700">
                                  {new Date(item.last_done_date).toLocaleDateString()}
                                  {item.last_done_mileage && ` at ${item.last_done_mileage.toLocaleString()} miles`}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* WHY - Due Reasons */}
                          {item.due_reasons && item.due_reasons.length > 0 && (
                            <div className="mt-1.5 pt-1.5 border-t border-slate-200">
                              <span className="text-slate-500">Why due: </span>
                              <span className="text-red-700 font-medium">{item.due_reasons.join('; ')}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Action button - only show if not already in pricing */}
                    {!alreadyInPricing && onAction && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleInclude(item);
                        }}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded bg-green-600 hover:bg-green-700 text-white transition-colors flex-shrink-0"
                      >
                        <Plus size={12} />
                        Include
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Main panel component
const ServiceRecommendationPanel = ({ 
  yestimateId, 
  onItemAdded,
  onRefresh,
  existingJobGroups = [],  // Pass existing job groups to check for duplicates
  addIntervalItemAsJob,    // Pass the actual add function from parent
  className = '',
  // Feb 2026 — Service Builder defaults to COLLAPSED on every job
  // type per FOH UX feedback ("service items are expanded on opening
  // pricing page is messy"). FOH clicks the header to expand when
  // they actually want to price a service interval. The legacy
  // ``defaultCollapsed`` prop is kept for callers that explicitly
  // need a different default; it now only overrides when set to
  // ``false`` (force-expanded).
  defaultCollapsed = true,
}) => {
  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deferredData, setDeferredData] = useState(null);
  const [showDeferred, setShowDeferred] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(!!defaultCollapsed);  // Service Builder — defaults to collapsed (FOH expands on demand)
  
  // LINKED ITEM DETECTION: Map keywords in parts/labour descriptions to recommendation item_ids
  // When PIE adds Springs, it includes Top Mount as part and Wheel Alignment as labour
  // We need to detect these and filter out related recommendations
  const LINKED_ITEM_KEYWORDS = {
    'front_top_mounts': ['top mount', 'strut mount', 'top mounts', 'strut mounts'],
    'rear_top_mounts': ['top mount', 'strut mount', 'top mounts', 'strut mounts'],
    'wheel_alignment': ['wheel alignment', 'alignment', 'tracking'],
    'front_springs': ['front spring', 'front springs', 'coil spring'],
    'rear_springs': ['rear spring', 'rear springs'],
    'front_shock_absorbers': ['front shock', 'front damper', 'shock absorber'],
    'rear_shock_absorbers': ['rear shock', 'rear damper', 'shock absorber'],
    'timing_belt': ['timing belt', 'cambelt', 'cam belt', 'timing kit'],
    'timing_belt_kit': ['timing belt kit', 'cambelt kit', 'cam belt kit'],
    'cam_belt': ['cam belt', 'cambelt', 'timing belt'],
  };
  
  // Build a comprehensive set of item IDs that are already in pricing
  const existingIntervalItemIds = useMemo(() => {
    const itemIds = new Set();
    
    existingJobGroups.forEach(jg => {
      // 1. Direct interval item matches
      if (jg.is_interval_item || jg.interval_item_id) {
        itemIds.add(jg.interval_item_id || jg.vi_item_key);
      }
      
      // 2. VI item key matches
      if (jg.vi_item_key) {
        itemIds.add(jg.vi_item_key);
        // Also handle auto-triggered items like "wheel_alignment_auto"
        if (jg.vi_item_key === 'wheel_alignment_auto') {
          itemIds.add('wheel_alignment');
        }
      }
      
      // 3. Detect linked items by scanning parts_lines and labour_lines
      const allDescriptions = [
        ...(jg.parts_lines || []).map(p => (p.description || '').toLowerCase()),
        ...(jg.labour_lines || []).map(l => (l.description || '').toLowerCase()),
        (jg.title || '').toLowerCase()
      ].join(' ');
      
      // Check each linked item's keywords
      Object.entries(LINKED_ITEM_KEYWORDS).forEach(([itemId, keywords]) => {
        if (keywords.some(keyword => allDescriptions.includes(keyword.toLowerCase()))) {
          itemIds.add(itemId);
        }
      });
    });
    
    return itemIds;
  }, [existingJobGroups]);
  
  // Fetch deferred items
  const fetchDeferredItems = async () => {
    if (!yestimateId) return;
    try {
      const response = await axios.get(`${API}/api/service-recommendation/deferred/${yestimateId}`);
      setDeferredData(response.data);
    } catch (err) {
      console.warn('Could not fetch deferred items:', err.message);
    }
  };
  
  // Fetch recommendation
  const fetchRecommendation = async (forceRebuild = false) => {
    if (!yestimateId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = forceRebuild 
        ? `${API}/api/service-recommendation/rebuild`
        : `${API}/api/service-recommendation/build`;
      
      const response = await axios.post(endpoint, {
        yestimate_id: yestimateId,
        force_rebuild: forceRebuild
      });
      
      setRecommendation(response.data);
    } catch (err) {
      console.error('Failed to fetch recommendation:', err);
      setError(err.response?.data?.detail || 'Failed to load service recommendation');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchRecommendation();
    fetchDeferredItems();
  }, [yestimateId]);
  
  // Check if item is already in pricing
  const isItemInPricing = (itemId) => {
    return existingIntervalItemIds.has(itemId);
  };
  
  // Handle FOH action
  const handleAction = async (itemId, action, section, item) => {
    if (!recommendation) return;
    
    // DUPLICATE PREVENTION: Check if already in pricing for Include action
    if (action === 'include_now' && isItemInPricing(itemId)) {
      toast.info('This item is already in pricing', { duration: 2000 });
      return;
    }
    
    setActionLoading(true);
    
    try {
      // Log analytics event FIRST
      await logRecommendationAction({
        recommendation_id: recommendation.recommendation_id,
        yestimate_id: yestimateId,
        item_id: itemId,
        action: action,
        section: section,
        item_class: item.item_class,
        confidence_level: item.confidence_level,
        previously_declined: item.previously_declined,
        recommendation_quality: recommendation.recommendation_quality
      });
      
      const response = await axios.post(`${API}/api/service-recommendation/action`, {
        recommendation_id: recommendation.recommendation_id,
        item_id: itemId,
        action: action
      });
      
      if (response.data.success) {
        // Handle Include action - add to pricing using parent's function
        if (action === 'include_now' && addIntervalItemAsJob) {
          // Build the item object expected by addIntervalItemAsJob
          const intervalItem = {
            item_id: item.item_id,
            label: item.item_label,
            description: item.description || item.explanation_text,
            category_id: item.category_id
          };
          
          // Call the parent's add function (package-aware)
          await addIntervalItemAsJob(item.category_id, intervalItem);
          
          toast.success(`"${item.item_label}" added to pricing`, { duration: 2500 });
        } else if (action === 'defer') {
          toast.info(`"${item.item_label}" set aside for now`, { duration: 2000 });
        } else if (action === 'already_done_elsewhere') {
          toast.success(`"${item.item_label}" marked as completed elsewhere`, { duration: 2000 });
        } else if (action === 'not_applicable') {
          toast.info(`"${item.item_label}" marked as not applicable`, { duration: 2000 });
        } else if (action === 'requires_discussion') {
          toast.info(`"${item.item_label}" flagged for discussion`, { duration: 2000 });
        }
        
        // Refresh recommendation to update state
        await fetchRecommendation(true);
        onRefresh?.();
      }
    } catch (err) {
      console.error('Action failed:', err);
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Log analytics event
  const logRecommendationAction = async (eventData) => {
    try {
      await axios.post(`${API}/api/service-recommendation/log-event`, eventData);
    } catch (err) {
      console.warn('Failed to log analytics event:', err.message);
      // Don't block the action if logging fails
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <div className={`bg-white rounded-lg border border-slate-200 p-6 ${className}`}>
        <div className="flex items-center justify-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={20} />
          <span>Building service recommendation...</span>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className={`bg-white rounded-lg border border-red-200 p-6 ${className}`}>
        <div className="flex items-center gap-2 text-red-600 mb-3">
          <AlertCircle size={20} />
          <span className="font-medium">Failed to load recommendation</span>
        </div>
        <p className="text-sm text-slate-600 mb-4">{error}</p>
        <button
          onClick={() => fetchRecommendation()}
          className="flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded text-sm"
        >
          <RefreshCw size={14} />
          Retry
        </button>
      </div>
    );
  }
  
  // No recommendation yet
  if (!recommendation) {
    return (
      <div className={`bg-white rounded-lg border border-slate-200 p-6 ${className}`}>
        <p className="text-slate-500 text-center">No service recommendation available</p>
      </div>
    );
  }
  
  const { 
    service_items_due = [], 
    service_items_history_unknown = [], 
    important_to_consider = [], 
    future_plan_next = [],
    alerts = [],
    recommendation_quality,
    quality_explanation,
    history_unknown_disclaimer,
    items_auto_included,
    total_items_evaluated
  } = recommendation;
  
  const hasSections = service_items_due.length > 0 || service_items_history_unknown.length > 0 || 
                      important_to_consider.length > 0 || future_plan_next.length > 0;
  
  // Filter out items already in pricing from each section
  const filterOutPriced = (items) => items.filter(i => !isItemInPricing(i.item_id));
  const filteredServiceDue = filterOutPriced(service_items_due);
  const filteredHistoryUnknown = filterOutPriced(service_items_history_unknown);
  const filteredImportant = filterOutPriced(important_to_consider);
  const filteredFuture = filterOutPriced(future_plan_next);
  
  const hasVisibleSections = filteredServiceDue.length > 0 || filteredHistoryUnknown.length > 0 || 
                             filteredImportant.length > 0 || filteredFuture.length > 0;
  
  // Build a set of all item_ids that are already displayed in sections
  // This prevents duplicates between alerts (e.g., "critical_overdue") and sections
  const sectionItemIds = new Set([
    ...service_items_due.map(i => i.item_id),
    ...service_items_history_unknown.map(i => i.item_id),
    ...important_to_consider.map(i => i.item_id),
    ...future_plan_next.map(i => i.item_id)
  ]);
  
  // Filter alert items to remove duplicates (items already shown in sections)
  // AND items already in pricing
  // AND history_quality alerts (already shown by QualityIndicator component)
  const filteredAlerts = alerts.map(alert => {
    // Skip history_quality alerts — QualityIndicator already shows this info
    if (alert.type === 'history_quality') {
      return null;
    }
    
    if (!alert.items || alert.items.length === 0) {
      return alert;
    }
    
    // Filter out items already in sections AND items already in pricing
    const filteredItems = alert.items.filter(item => 
      !sectionItemIds.has(item.item_id) && !isItemInPricing(item.item_id)
    );
    
    // If all items were filtered out, don't show the alert
    if (filteredItems.length === 0 && alert.items.length > 0) {
      return null;
    }
    
    // Update the message count if items were filtered
    let message = alert.message;
    if (filteredItems.length !== alert.items.length && alert.type === 'critical_overdue') {
      message = filteredItems.length > 0 
        ? `${filteredItems.length} additional overdue item(s) to review`
        : alert.message;
    }
    
    return {
      ...alert,
      items: filteredItems,
      message
    };
  }).filter(Boolean); // Remove null alerts
  
  return (
    <div className={`bg-white rounded-lg border border-slate-200 shadow-sm ${className}`} data-testid="service-recommendation-panel">
      {/* Header — clickable to collapse. Renamed to "Service Builder"
          (Feb 2026) per FOH UX feedback: title now matches the FOH
          mental model (they're *building* the service quote, not just
          reading a recommendation). Header is intentionally compact
          and visually distinct so FOH can scan the pricing screen at a
          glance and find the Service Builder quickly. */}
      <div
        className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors rounded-t-lg bg-gradient-to-r from-slate-50 to-white"
        onClick={() => setPanelCollapsed(!panelCollapsed)}
        data-testid="service-builder-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
            <Sparkles size={14} className="text-blue-600" />
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-slate-800 text-sm leading-tight" data-testid="service-builder-title">Service Builder</h2>
            <p className="text-[11px] text-slate-500 leading-tight truncate">
              {items_auto_included} of {total_items_evaluated} auto-included
              {(() => {
                const inPricingCount = [...service_items_due, ...service_items_history_unknown, ...important_to_consider, ...future_plan_next].filter(i => isItemInPricing(i.item_id)).length;
                return inPricingCount > 0 ? ` · ${inPricingCount} in pricing` : '';
              })()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              fetchRecommendation(true);
            }}
            disabled={loading || actionLoading}
            className="flex items-center gap-1 px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 rounded transition-colors"
            data-testid="service-builder-rebuild"
          >
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} />
            Rebuild
          </button>
          {panelCollapsed ? (
            <ChevronDown size={16} className="text-slate-400" data-testid="service-builder-chevron-down" />
          ) : (
            <ChevronUp size={16} className="text-slate-400" data-testid="service-builder-chevron-up" />
          )}
        </div>
      </div>
      
      {/* Collapsible content */}
      {!panelCollapsed && (
        <div className="p-4">
        {/* Quality indicator */}
        <div className="mb-4">
          <QualityIndicator quality={recommendation_quality} explanation={quality_explanation} />
        </div>
        
        {/* Alerts - filtered to remove items already shown in sections */}
        {filteredAlerts.length > 0 && (
          <div className="space-y-2 mb-4">
            {filteredAlerts.map((alert, idx) => (
              <RecommendationAlert 
                key={idx} 
                alert={alert} 
                items={alert.items || []}
                defaultExpanded={false}
                onAction={handleAction}
                isItemInPricing={isItemInPricing}
              />
            ))}
          </div>
        )}
        
        {/* Sections — items already in pricing are hidden */}
        {hasVisibleSections ? (
          <>
            {/* Service Items Due - Confirmed overdue */}
            {filteredServiceDue.length > 0 && (
            <RecommendationSection
              sectionKey="service_items_due"
              items={filteredServiceDue}
              onAction={handleAction}
              isLoading={actionLoading}
              defaultExpanded={true}
              isItemInPricing={isItemInPricing}
            />
            )}
            
            {/* Service Items - History Unknown with Disclaimer */}
            {filteredHistoryUnknown.length > 0 && (
              <div className="mb-4">
                <RecommendationSection
                  sectionKey="service_items_history_unknown"
                  items={filteredHistoryUnknown}
                  onAction={handleAction}
                  isLoading={actionLoading}
                  defaultExpanded={true}
                  isItemInPricing={isItemInPricing}
                />
                {/* Customer-facing disclaimer */}
                {history_unknown_disclaimer && (
                  <div className="mt-2 mx-1 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800">
                    <div className="flex items-start gap-2">
                      <Info size={14} className="flex-shrink-0 mt-0.5" />
                      <p className="italic">{history_unknown_disclaimer}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Important to Consider */}
            {filteredImportant.length > 0 && (
            <RecommendationSection
              sectionKey="important_to_consider"
              items={filteredImportant}
              onAction={handleAction}
              isLoading={actionLoading}
              defaultExpanded={true}
              isItemInPricing={isItemInPricing}
            />
            )}
            
            {/* Future / Plan Next */}
            {filteredFuture.length > 0 && (
            <RecommendationSection
              sectionKey="future_plan_next"
              items={filteredFuture}
              onAction={handleAction}
              isLoading={actionLoading}
              defaultExpanded={false}
              isItemInPricing={isItemInPricing}
            />
            )}
          </>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <CheckCircle2 size={32} className="mx-auto mb-2 text-green-500" />
            <p className="font-medium">All service items up to date</p>
            <p className="text-sm">No service maintenance due at this time</p>
          </div>
        )}
        
        {/* EXPANDABLE PREVIOUS DECISIONS SECTION */}
        {deferredData && (deferredData.deferred_count > 0 || deferredData.suppressed_count > 0) && (
          <div className="mt-4 rounded-lg border border-slate-300 overflow-hidden">
            <button
              onClick={() => setShowDeferred(!showDeferred)}
              className="w-full bg-slate-100 px-4 py-3 flex items-center justify-between hover:bg-slate-150 transition-colors"
            >
              <div className="flex items-center gap-2">
                <History size={16} className="text-slate-500" />
                <span className="font-medium text-sm text-slate-700">Previous Decisions / Done Elsewhere</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded-full">
                  {(deferredData.deferred_count || 0) + (deferredData.suppressed_count || 0)} item(s)
                </span>
                {showDeferred ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </div>
            </button>
            
            {showDeferred && (
              <div className="bg-slate-50 p-3 space-y-2 max-h-[300px] overflow-y-auto">
                {/* Not actioned items */}
                {deferredData.deferred_items?.map((item, idx) => (
                  <div key={`deferred-${idx}`} className="bg-white border border-slate-200 rounded-md p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">{item.item_label || item.item_id}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                        Not Actioned
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span>Date: {item.declined_at ? new Date(item.declined_at).toLocaleDateString() : 'N/A'}</span>
                      {item.deferred_by && <span>By: {item.deferred_by}</span>}
                    </div>
                  </div>
                ))}
                
                {/* Suppressed items (Done Elsewhere / N/A) */}
                {deferredData.suppressed_items?.map((item, idx) => (
                  <div key={`suppressed-${idx}`} className="bg-white border border-slate-200 rounded-md p-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">{item.item_label || item.item_id}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        item.suppression_type === 'done_elsewhere' 
                          ? 'bg-blue-100 text-blue-700' 
                          : 'bg-slate-200 text-slate-600'
                      }`}>
                        {item.suppression_type === 'done_elsewhere' ? 'Done Elsewhere' : 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span>Until: {item.suppress_until ? new Date(item.suppress_until).toLocaleDateString() : 'N/A'}</span>
                      {item.suppressed_by && <span>By: {item.suppressed_by}</span>}
                    </div>
                  </div>
                ))}
                
                {(deferredData.deferred_count === 0 && deferredData.suppressed_count === 0) && (
                  <p className="text-center text-sm text-slate-500 py-4">No previous decisions</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default ServiceRecommendationPanel;
